import {
  Button,
  colors,
  Heading,
  Message,
  PageContainer,
  Paragraph,
  setAppBackgroundColor,
  spacing,
  Spinner,
} from 'component-library';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { formatNumberToCurrency } from '@sr/utils';
import { type ServiceUser } from 'core/lib/types';
import { useProfileV2 } from 'core/lib/Profile';
import { type SubscriptionDetails } from '../../SubscriptionsProvider/SubscriptionsContext';
import { useSubscriptions } from '../../SubscriptionsProvider';
import {
  HeroContainer,
  HeroPageContainer,
  Label,
  OrderDetailsColumn,
  OrderDetailsContainer,
  OrderDetailsDivider,
  OrderDetailsRow,
  OrderDetailsValue,
  SubText,
} from './styles';
import { type ConfirmationOrder, type ConfirmationPartner } from './types';

// Re-export styles for reuse in other confirmation pages
export {
  HeroContainer,
  HeroPageContainer,
  Label,
  OrderDetailsColumn,
  OrderDetailsContainer,
  OrderDetailsDivider,
  OrderDetailsRow,
  OrderDetailsValue,
  SubText,
} from './styles';

export type ConfirmationButton = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
};

export type ConfirmationApiHooks = {
  getOrder: (orderId: string) => Promise<ConfirmationOrder>;
  getUserById: (userId: string) => Promise<ServiceUser>;
  getPartnerInfo: (partnerIds: string[]) => Promise<ConfirmationPartner[]>;
};

export type ConfirmationPageProps = {
  orderId: string;
};

export const ConfirmationPage: React.FC<ConfirmationPageProps> = ({ orderId }) => {
  setAppBackgroundColor(colors.base.white);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { profile } = useProfileV2();
  const { getSubscriptionDetailsById } = useSubscriptions();
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);

  const getSubscriptionDetails = useCallback(
    async (orderId: string) => {
      setError(false);
      setLoading(true);
      try {
        const subscriptionDetails = await getSubscriptionDetailsById(orderId);

        setSubscriptionDetails(subscriptionDetails);
      } catch (err) {
        console.error('Failed to fetch subscription details', err);
        setError(true);
      }
      setLoading(false);
    },
    [getSubscriptionDetailsById],
  );

  useEffect(() => {
    void getSubscriptionDetails(orderId);
  }, [orderId, getSubscriptionDetails]);

  if (loading) {
    return (
      <PageContainer>
        <Spinner size="large" />
      </PageContainer>
    );
  }

  if (error || !subscriptionDetails?.order) {
    return (
      <PageContainer>
        <Message
          severity="error"
          title={t('errorMessage.title')}
          description={t('errorMessage.description')}
        />
      </PageContainer>
    );
  }

  const priceInfo = { vat: subscriptionDetails.order.vatRate };
  const orderDate = dayjs(subscriptionDetails.order.createdDate).format('YYYY-MM-DD');
  const vat = subscriptionDetails.order.price * priceInfo.vat;
  const total = subscriptionDetails.order.price + vat;

  const getAddressString = (address: ConfirmationPartner['address'] | undefined): string => {
    let addressString = '';

    if (address?.street) {
      addressString += address.street;
    }
    if (address?.city && address.city !== '.') {
      addressString += `, ${address.city}`;
    }

    return addressString;
  };

  let recipient = subscriptionDetails.user.email;

  if (profile?.userId === subscriptionDetails.user.id) {
    recipient = t('you');
  } else if (subscriptionDetails.user.fullName) {
    recipient = subscriptionDetails.user.fullName;
  }

  return (
    <>
      <HeroContainer>
        <HeroPageContainer>
          <Heading as="h1" variant="xLarge" mb={spacing.xsmall}>
            {t(`subscription.confirmation.title.${subscriptionDetails.order.type.toLowerCase()}`)}
          </Heading>
          <Paragraph variant="large" mb="mediumLow">
            {t(`subscription.confirmation.text.${subscriptionDetails.order.type.toLowerCase()}`, {
              recipient,
            })}
          </Paragraph>
          {subscriptionDetails.order.type === 'CSRD_PREMIUM' && (
            <Button
              variant="primary"
              onClick={() => {
                void navigate('/csrd');
              }}
            >
              {t('subscription.confirmation.open_csrd', 'Open CSRD')}
            </Button>
          )}
        </HeroPageContainer>
      </HeroContainer>
      <PageContainer>
        <Heading as="h2" variant="large">
          {t('subscription.confirmation.subtitle')}
        </Heading>
        <SubText>
          {t('subscription.confirmation.subtext')} {orderDate}
        </SubText>
        <OrderDetailsContainer p="small">
          <OrderDetailsColumn>
            <Paragraph variant="large" bold>
              {t(`subscription.plan.${subscriptionDetails.order.type.toLowerCase()}`)}
            </Paragraph>
            <OrderDetailsValue variant="large">
              {(() => {
                if (subscriptionDetails.order.billingCycle === 'annually') {
                  return t('invoiced.annually');
                }

                return t('invoiced');
              })()}
            </OrderDetailsValue>
          </OrderDetailsColumn>
          <OrderDetailsRow>
            <OrderDetailsColumn>
              <Label>{t('subscription.confirmation.price')}</Label>
              <OrderDetailsValue variant="large">
                {formatNumberToCurrency(
                  subscriptionDetails.order.price,
                  subscriptionDetails.order.currency,
                )}
              </OrderDetailsValue>
            </OrderDetailsColumn>
            <OrderDetailsColumn>
              <Label>{t('subscription.confirmation.vat')}</Label>
              <OrderDetailsValue variant="large">
                {formatNumberToCurrency(vat.toFixed(2), subscriptionDetails.order.currency)}
              </OrderDetailsValue>
            </OrderDetailsColumn>
            <OrderDetailsColumn>
              <Label bold color={colors.base.black}>
                {t('subscription.confirmation.total')}
              </Label>
              <OrderDetailsValue variant="large">
                {formatNumberToCurrency(total.toFixed(2), subscriptionDetails.order.currency)}
              </OrderDetailsValue>
            </OrderDetailsColumn>
          </OrderDetailsRow>
          <OrderDetailsDivider />
          <OrderDetailsRow>
            <OrderDetailsColumn>
              <Label>{t('subscription.confirmation.purchased.by')}</Label>
              <>
                <OrderDetailsValue variant="large">
                  {subscriptionDetails.purchaser.fullName}
                </OrderDetailsValue>
                <OrderDetailsValue variant="large">
                  {subscriptionDetails.purchaser.email}
                </OrderDetailsValue>
                {subscriptionDetails.purchaser.phone && (
                  <OrderDetailsValue variant="large">
                    {subscriptionDetails.purchaser.phone}
                  </OrderDetailsValue>
                )}
              </>
            </OrderDetailsColumn>
            <OrderDetailsColumn>
              <Label>{t('subscription.confirmation.purchased.for')}</Label>
              <OrderDetailsValue variant="large">
                {subscriptionDetails.user.fullName}
              </OrderDetailsValue>
              <OrderDetailsValue variant="large">
                {subscriptionDetails.user.email}
              </OrderDetailsValue>
              {subscriptionDetails.user.phone && (
                <OrderDetailsValue variant="large">
                  {subscriptionDetails.user.phone}
                </OrderDetailsValue>
              )}
            </OrderDetailsColumn>
            <OrderDetailsColumn>
              <Label>{t('subscription.confirmation.invoice.recipient')}</Label>
              <OrderDetailsValue variant="large">
                {subscriptionDetails.invoicePartner.name}
              </OrderDetailsValue>
              <OrderDetailsValue variant="large">
                {getAddressString(subscriptionDetails.invoicePartner.address)}
              </OrderDetailsValue>
              <OrderDetailsValue variant="large">
                {subscriptionDetails.invoicePartner.id}
              </OrderDetailsValue>
            </OrderDetailsColumn>
          </OrderDetailsRow>
        </OrderDetailsContainer>
      </PageContainer>
    </>
  );
};
